import { useEffect, useState } from "react";
import client, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Download, Trash2, Archive } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";

function formatBytes(n) {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function Imports() {
  const { user } = useAuth();
  const canDeleteArchives = hasGlobalPermission(user, PERM.IMPORTS_ARCHIVES_DELETE);

  const [items, setItems] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = () => {
    client.get(`/admin/feed-archives`).then((r) => setItems(r.data)).catch(() => setItems([]));
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (archive) => {
    if (!window.confirm(`Delete archive "${archive.filename}"? This cannot be undone.`)) return;
    setDeletingId(archive.id);
    try {
      await client.delete(`/admin/feed-archives/${archive.id}`);
      setItems((prev) => prev.filter((a) => a.id !== archive.id));
      toast.success("Archive deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="imports-page">
      <div className="mb-8">
        <p className="data-label mb-2">DEALER FEED · IMPORTS</p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Imports</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Every dealer feed import run stores the exact JSON + XML payloads it fetched, zipped, for
          history and auditing. Archives older than 365 days are deleted automatically on the next
          import run.
        </p>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="imports-table">
            <thead className="thead">
              <tr>
                <th className="px-4 py-2 text-left">Created</th>
                <th className="px-4 py-2 text-left">Filename</th>
                <th className="px-4 py-2 text-left">Session</th>
                <th className="px-4 py-2 text-left">Size</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!items &&
                ["s1", "s2", "s3"].map((k) => (
                  <tr key={k} className="border-t border-border">
                    <td colSpan={5} className="px-4 py-3">
                      <Skeleton className="h-5 w-full" />
                    </td>
                  </tr>
                ))}
              {items &&
                items.map((a) => (
                  <tr key={a.id} className="row-hover border-t border-border" data-testid={`import-row-${a.id}`}>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums text-muted-foreground">
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{a.filename}</td>
                    <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                      {a.session_id?.slice(0, 8)}…
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums text-muted-foreground">
                      {formatBytes(a.size_bytes)}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex justify-end gap-1">
                        <Button asChild variant="ghost" size="icon" data-testid={`import-download-${a.id}`}>
                          <a href={`${API}/admin/feed-archives/${a.id}/download`} download={a.filename}>
                            <Download className="h-3.5 w-3.5" />
                          </a>
                        </Button>
                        {canDeleteArchives && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`import-delete-${a.id}`}
                            disabled={deletingId === a.id}
                            onClick={() => remove(a)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              {items && items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-16 text-center text-sm text-muted-foreground">
                    <div className="flex flex-col items-center gap-2">
                      <Archive className="h-6 w-6 text-muted-foreground/60" />
                      No feed archives yet — run a dealer feed import from Settings to create one.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
